import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../store";
import { fetchCategoriesByBalance, resetCategoriesByBalance } from "../../../../store/slices/categoriesByBalance";
import Input from "../../../ui/common/Input";
import Select from "../../../ui/select/Select";
import Button from "../../../ui/common/Button";
import { EditIcon, DeleteIcon, PlusIcon, SaveIcon, CloseIcon } from "../../../../icons/Icons";
import Api from "../../../../store/api/Api";
import useCheckUserPermissions from "../../../../customHooks/useCheckUserPermissions";
import "./ChannelOptimalisationModal.scss";

interface Benchmark {
  id?: string;
  brandId?: string;
  balance: string;
  category: string;
  path: string;
  min: number;
  max: number;
  target?: number;
  color?: string;
  readabilityRange?: {
    min: number;
    max: number;
  };
  timestamp?: string;
}

interface ChannelOptimalisationModalProps {
  brandId: string;
  brandName: string;
  onClose: () => void;
  hideActions?: boolean;
}

const ChannelOptimalisationModal = ({ brandId, brandName, onClose, hideActions = false }: ChannelOptimalisationModalProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const emptyBenchmark: Benchmark = {
    balance: "",
    category: "",
    path: "",
    min: 0,
    max: 100,
    target: 50,
    color: "",
    readabilityRange: {
      min: 1,
      max: 10
    }
  };

  const balanceDropdownOptions = useMemo(() => ["user_needs", "video", "sentiment", "access_level", "readability"], []);

  const [benchmarkEditIndex, setBenchmarkEditIndex] = useState<number | null>(null);
  const [benchmarkEditData, setBenchmarkEditData] = useState<Benchmark>(emptyBenchmark);
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBalance, setSelectedBalance] = useState<string>(searchParams.get("balance") || "All Balances");
  const [selectedPath, setSelectedPath] = useState<string>(searchParams.get("path") || "All Paths");
  const [colourError, setColourError] = useState<string | null>(null);
  const api = useMemo(() => new Api(), []);
  const benchmarkBodyRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const checkIfAllowed = useCheckUserPermissions();
  const isAdmin = checkIfAllowed("admin");

  const categories = useSelector((state: any) => state.categoriesByBalance.data);
  const loadingCategories = useSelector((state: any) => state.categoriesByBalance.isLoading);

  // Helper function to format display names (replace underscores with spaces)
  const formatDisplayName = useCallback((value: string): string => {
    return value ? value.replace(/_/g, ' ') : value;
  }, []);

  // Helper function to validate hex colour
  const isValidHexColour = (colour: string): boolean => {
    if (!colour) return true; // Empty is valid
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexRegex.test(colour);
  };

  // Create formatted category options with separate value and label
  const categoryOptions = useMemo(() => {
    return categories.map((cat: string) => ({
      value: cat,
      label: formatDisplayName(cat)
    }));
  }, [categories, formatDisplayName]);

  const balanceOptions = useMemo(() => {
    const balances = benchmarks.map(b => b.balance).filter(Boolean);
    const uniqueBalances = Array.from(new Set(balances)).sort();
    return ["All Balances", ...uniqueBalances];
  }, [benchmarks]);

  // Create formatted balance options for editing (with spaces instead of underscores)
  const balanceEditOptions = useMemo(() => {
    return balanceDropdownOptions.map((bal: string) => ({
      value: bal,
      label: formatDisplayName(bal)
    }));
  }, [balanceDropdownOptions, formatDisplayName]);

  const pathOptions = useMemo(() => {
    const paths = benchmarks.map(b => b.path).filter(Boolean);
    const uniquePaths = Array.from(new Set(paths)).sort();
    return ["All Paths", ...uniquePaths];
  }, [benchmarks]);

  const filteredBenchmarks = useMemo(() => {
    let filtered = benchmarks;

    if (selectedBalance !== "All Balances") {
      filtered = filtered.filter(b => b.balance === selectedBalance);
    }

    if (selectedPath !== "All Paths") {
      filtered = filtered.filter(b => b.path === selectedPath);
    }
    
    // Sort by balance (Main Category) by default
    filtered = [...filtered].sort((a, b) => {
      const balanceA = a.balance || "";
      const balanceB = b.balance || "";
      
      // Sort alphabetically by balance name
      if (balanceA < balanceB) return -1;
      if (balanceA > balanceB) return 1;
      
      // If balances are the same, sort by category
      const categoryA = a.category || "";
      const categoryB = b.category || "";
      
      if (categoryA < categoryB) return -1;
      if (categoryA > categoryB) return 1;
      
      return 0;
    });
    
    return filtered;
  }, [benchmarks, selectedBalance, selectedPath]);

  const fetchBenchmarks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getBenchmarks(brandId);
      const benchmarksData = Array.isArray(response) ? response : (response.data || []);
      setBenchmarks(benchmarksData);
    } catch (err: any) {
      setError(err.message || "Failed to fetch benchmarks");
      console.error("Error fetching benchmarks:", err);
    } finally {
      setLoading(false);
    }
  }, [api, brandId]);

  useEffect(() => {
    fetchBenchmarks();
  }, [fetchBenchmarks]);

  useEffect(() => {
    if (benchmarkEditIndex !== null && benchmarkEditData.balance) {
      dispatch(fetchCategoriesByBalance(brandId, benchmarkEditData.balance));
    } else {
      dispatch(resetCategoriesByBalance());
    }
  }, [benchmarkEditData.balance, benchmarkEditIndex, brandId, dispatch]);

  useEffect(() => {
    if (categories.length > 0 && !benchmarkEditData.category) {
      setBenchmarkEditData((prev) => ({ ...prev, category: categories[0] }));
    }
  }, [categories, benchmarkEditData.category]);

  useEffect(() => {
    const params = new URLSearchParams();

    if (selectedBalance !== "All Balances") {
      params.set("balance", selectedBalance);
    }

    if (selectedPath !== "All Paths") {
      params.set("path", selectedPath);
    }

    setSearchParams(params, { replace: true });
  }, [selectedBalance, selectedPath, setSearchParams]);

  // When a new benchmark is added (benchmarkEditIndex === -1), find its index in filteredBenchmarks
  useEffect(() => {
    if (benchmarkEditIndex === -1 && benchmarkEditData) {
      // Find the new benchmark in filteredBenchmarks (it won't have an ID)
      const newBenchmarkIndex = filteredBenchmarks.findIndex(b => 
        !b.id && 
        b.balance === benchmarkEditData.balance && 
        b.category === benchmarkEditData.category &&
        b.path === benchmarkEditData.path &&
        b.min === benchmarkEditData.min &&
        b.max === benchmarkEditData.max
      );
      
      if (newBenchmarkIndex !== -1) {
        setBenchmarkEditIndex(newBenchmarkIndex);
      }
    }
  }, [benchmarkEditIndex, benchmarkEditData, filteredBenchmarks]);

  const handleInputChange = (value: any, field: keyof Benchmark | string) => {
    if (field === "readabilityRangeMin") {
      setBenchmarkEditData({
        ...benchmarkEditData,
        readabilityRange: {
          min: value,
          max: benchmarkEditData.readabilityRange?.max || 10
        }
      });
    } else if (field === "readabilityRangeMax") {
      setBenchmarkEditData({
        ...benchmarkEditData,
        readabilityRange: {
          min: benchmarkEditData.readabilityRange?.min || 1,
          max: value
        }
      });
    } else if (field === "target") {
      const newTarget = Math.max(1, Math.min(100, parseInt(value) || 1));
      
      setBenchmarkEditData({
        ...benchmarkEditData,
        target: newTarget,
      });
    } else if (field === "color") {
      const colourValue = value as string;
      
      // Validate hex color
      if (colourValue && !isValidHexColour(colourValue)) {
        setColourError("Voer een geldige hex-kleurcode in, bijv. #FF5733.");
      } else {
        setColourError(null);
      }
      
      setBenchmarkEditData({
        ...benchmarkEditData,
        color: colourValue,
      });
    } else {
      setBenchmarkEditData({
        ...benchmarkEditData,
        [field]: value,
      });
    }
  };

  const handleBenchmarkSave = async () => {
    if (!benchmarkEditData.balance || !benchmarkEditData.category) {
      setError("Balance and Category are required fields");
      return;
    }

    if (benchmarkEditData.min > benchmarkEditData.max) {
      setError("Minimum value cannot be greater than maximum value");
      return;
    }

    // Validate color if provided
    if (benchmarkEditData.color && !isValidHexColour(benchmarkEditData.color)) {
      setColourError("Voer een geldige hex-kleurcode in, bijv. #FF5733.");
      return;
    }

    // Get the benchmark being edited from filteredBenchmarks
    // If benchmarkEditIndex is -1, this is a new benchmark being created
    const editingBenchmark = benchmarkEditIndex !== null && benchmarkEditIndex >= 0 ? filteredBenchmarks[benchmarkEditIndex] : null;
    const editingBenchmarkId = editingBenchmark?.id;
    const isNewBenchmark = benchmarkEditIndex === -1;

    // Check for readability range overlap (only for readability balance)
    if (benchmarkEditData.balance === "readability") {
      const readabilityOverlap = benchmarks.find((benchmark) => {
        // Skip the current benchmark being edited (compare by ID)
        if (editingBenchmarkId && benchmark.id === editingBenchmarkId) return false;
        
        // Only check other readability benchmarks
        if (benchmark.balance !== "readability") return false;
        
        const samePath = benchmark.path === benchmarkEditData.path || benchmark.path === "*" || benchmarkEditData.path === "*";
        if (!samePath) return false;
        
        // Skip if the existing benchmark doesn't have a readabilityRange yet
        if (!benchmark.readabilityRange) return false;
        
        const newReadabilityMin = benchmarkEditData.readabilityRange?.min || 1;
        const newReadabilityMax = benchmarkEditData.readabilityRange?.max || 10;
        const existingReadabilityMin = benchmark.readabilityRange?.min || 1;
        const existingReadabilityMax = benchmark.readabilityRange?.max || 10;
        
        return !(newReadabilityMax < existingReadabilityMin || newReadabilityMin > existingReadabilityMax);
      });

      if (readabilityOverlap) {
        setError(`Readability range overlaps with existing benchmark: ${readabilityOverlap.category} (${readabilityOverlap.readabilityRange?.min || 1} - ${readabilityOverlap.readabilityRange?.max || 10})`);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const payload: any = {
        balance: benchmarkEditData.balance,
        category: benchmarkEditData.category,
        path: benchmarkEditData.path,
        min: benchmarkEditData.min,
        max: benchmarkEditData.max,
        target: benchmarkEditData.target || 50
      };
      
      // Add color if admin and provided
      if (isAdmin) {
        payload.color = benchmarkEditData.color || "";
      }
      
      // Add readabilityRange for readability benchmarks
      if (benchmarkEditData.balance === "readability") {
        // Use the readabilityRange from edit data, or preserve from existing benchmark
        const readabilityRange = benchmarkEditData.readabilityRange || editingBenchmark?.readabilityRange || { min: 1, max: 10 };
        payload.readabilityRange = readabilityRange;
      }
      
      if (!isNewBenchmark && editingBenchmark?.id) {
        // Update existing benchmark
        await api.postUpdateBenchmark(editingBenchmark.id!, payload);
        
        // Update only the specific item in the array by ID
        setBenchmarks(prevBenchmarks => 
          prevBenchmarks.map(benchmark => 
            benchmark.id === editingBenchmark.id
              ? { 
                  ...benchmarkEditData, 
                  id: editingBenchmark.id,
                  brandId: editingBenchmark.brandId,
                  readabilityRange: benchmarkEditData.readabilityRange || benchmark.readabilityRange,
                  timestamp: new Date().toISOString()
                }
              : benchmark
          )
        );
      } else {
        // Create new benchmark
        payload['brandId'] = brandId;
        const newBenchmark = await api.postCreateBenchmark(payload);
        
        // Find and replace the virtual benchmark in the original benchmarks array
        const virtualBenchmarkIndex = benchmarks.findIndex(b => 
          !b.id && 
          b.balance === editingBenchmark?.balance && 
          b.category === editingBenchmark?.category && 
          b.path === editingBenchmark?.path &&
          b.min === editingBenchmark?.min &&
          b.max === editingBenchmark?.max
        );
        
        if (virtualBenchmarkIndex !== -1) {
          // Replace virtual benchmark with real one
          setBenchmarks(prevBenchmarks => 
            prevBenchmarks.map((benchmark, index) => 
              index === virtualBenchmarkIndex
                ? { 
                    ...benchmarkEditData, 
                    id: newBenchmark.id || `temp_${Date.now()}`,
                    brandId: brandId,
                    timestamp: new Date().toISOString()
                  }
                : benchmark
            )
          );
        } else {
          // Fallback: add new benchmark if virtual not found
          setBenchmarks(prevBenchmarks => [
            ...prevBenchmarks,
            { 
              ...benchmarkEditData, 
              id: newBenchmark.id || `temp_${Date.now()}`,
              brandId: brandId,
              timestamp: new Date().toISOString()
            }
          ]);
        }
      }
      
      setBenchmarkEditIndex(null);
    } catch (err: any) {
      setError(err.message || "Failed to save benchmark");
      console.error("Error saving benchmark:", err);
      
      // Remove the virtual benchmark if save failed and it was a new benchmark
      if (isNewBenchmark) {
        // Find and remove the virtual benchmark from the array
        const originalIndex = benchmarks.findIndex(b => 
          !b.id &&
          b.balance === benchmarkEditData.balance && 
          b.category === benchmarkEditData.category && 
          b.path === benchmarkEditData.path &&
          b.min === benchmarkEditData.min &&
          b.max === benchmarkEditData.max
        );
        
        if (originalIndex !== -1) {
          setBenchmarks(prevBenchmarks => 
            prevBenchmarks.filter((_, index) => index !== originalIndex)
          );
        }
        setBenchmarkEditIndex(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBenchmarkCancel = () => {
    if (benchmarkEditData.balance === "" && benchmarkEditData.category === "" && benchmarkEditIndex !== null) {
      // Find the virtual benchmark in the original array and remove it
      const editingBenchmark = filteredBenchmarks[benchmarkEditIndex];
      if (editingBenchmark && !editingBenchmark.id) {
        const originalIndex = benchmarks.findIndex(b => 
          !b.id &&
          b.balance === editingBenchmark.balance && 
          b.category === editingBenchmark.category && 
          b.path === editingBenchmark.path &&
          b.min === editingBenchmark.min &&
          b.max === editingBenchmark.max
        );
        
        if (originalIndex !== -1) {
          setBenchmarks(prevBenchmarks => 
            prevBenchmarks.filter((_, index) => index !== originalIndex)
          );
        }
      }
    }
    setBenchmarkEditIndex(null);
    setError(null);
    setColourError(null);
  };

  const handleBenchmarkDelete = async (key: number) => {
    // Get the actual benchmark from filteredBenchmarks using the key
    const filteredBenchmark = filteredBenchmarks[key];
    
    if (!filteredBenchmark) {
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this benchmark?");
    if (!confirmed) {
      return;
    }

    if (!filteredBenchmark?.id) {
      // For virtual benchmarks (no ID), remove from filtered list
      // Find the corresponding index in the original benchmarks array
      const originalIndex = benchmarks.findIndex(b => 
        b.balance === filteredBenchmark.balance && 
        b.category === filteredBenchmark.category && 
        b.path === filteredBenchmark.path &&
        b.min === filteredBenchmark.min &&
        b.max === filteredBenchmark.max
      );
      
      if (originalIndex !== -1) {
        const updatedBenchmarks = benchmarks.filter((_, index) => index !== originalIndex);
        setBenchmarks(updatedBenchmarks);
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.deleteBenchmark(filteredBenchmark.id);
      
      // Remove the benchmark by finding it in the original array
      setBenchmarks(prevBenchmarks => 
        prevBenchmarks.filter(benchmark => benchmark.id !== filteredBenchmark.id)
      );
    } catch (err: any) {
      setError(err.message || "Failed to delete benchmark");
      console.error("Error deleting benchmark:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBenchmarkNew = () => {
    // Create a fresh copy of emptyBenchmark to avoid reference issues
    const newBenchmark = { ...emptyBenchmark };
    const updatedBenchmarks = [...benchmarks, newBenchmark];
    
    setBenchmarks(updatedBenchmarks);
    setBenchmarkEditData(newBenchmark);
    
    // Set a temporary marker to identify this as a new benchmark being created
    // We'll set the actual index after the next render when filteredBenchmarks updates
    setBenchmarkEditIndex(-1); // Use -1 as a marker for "new benchmark"
  };

  const handleSave = () => {
    onClose();
  };

  return (
    <div className={`co-modal-wrapper ${hideActions ? "full-width" : ""}`}>
      <h3>Channel Optimisation - Benchmarks - {brandName}</h3>
      <p className="co-description">
        Manage benchmarks for channel optimization. Each benchmark defines target ranges for content categories.
      </p>

      {error && <div className="error-message">{error}</div>}

      <div className="filter-container">
        <div className="filter-item">
          <label htmlFor="balance-filter">Filter by Balance:</label>
          <Select
            options={balanceOptions}
            selectedOption={selectedBalance}
            setSelectedOption={setSelectedBalance}
            fullWidth={false}
          />
        </div>
        <div className="filter-item">
          <label htmlFor="path-filter">Filter by Path:</label>
          <Select
            options={pathOptions}
            selectedOption={selectedPath}
            setSelectedOption={setSelectedPath}
            fullWidth={false}
          />
        </div>
      </div>

      <div className="benchmarks-container">
        <div className="benchmark-header">
          <div className="benchmark-balance">
            <p>Balance (Main Category)</p>
          </div>
          <div className="benchmark-category">
            <p>Category (Subcategory)</p>
          </div>
          <div className="benchmark-path">
            <p>Path <em>(* all paths)</em></p>
          </div>
          <div className="benchmark-range">
            <p>Range (Min-Max %)</p>
          </div>
          <div className="benchmark-target">
            <p>Target (%)</p>
          </div>
          <div className="benchmark-readability-range">
            <p>Readability Range</p>
          </div>
          {isAdmin && (
            <div className="benchmark-color">
              <p>Color</p>
            </div>
          )}
          <div className="benchmark-buttons"></div>
        </div>
        <div className="benchmark-body" ref={benchmarkBodyRef}>
          {loading ? (
            <div className="loading-message">Loading benchmarks...</div>
          ) : filteredBenchmarks.length === 0 ? (
            <div className="empty-message">{selectedBalance === "All Balances" ? "No benchmarks configured. Click + to add one." : `No benchmarks found for balance: ${selectedBalance}`}</div>
          ) : (
            filteredBenchmarks.map((benchmark, key) => (
              <div className="benchmark" key={key}>
                <div
                  className="benchmark-balance"
                  onClick={() => {
                    if (benchmarkEditIndex === key) {
                      setTimeout(() => {
                        const benchmarkElement = benchmarkBodyRef.current?.children[key] as HTMLElement;
                        if (benchmarkElement && benchmarkBodyRef.current) {
                          const elementTop = benchmarkElement.offsetTop;
                          const containerHeight = benchmarkBodyRef.current.clientHeight;
                          const scrollPosition = elementTop - containerHeight + benchmarkElement.clientHeight + 200;
                          benchmarkBodyRef.current.scrollTop = Math.max(0, scrollPosition);
                        }
                      }, 50);
                    }
                  }}
                >
                  {benchmarkEditIndex === key ? (
                    <Select
                      options={balanceEditOptions}
                      selectedOption={formatDisplayName(benchmarkEditData?.balance) || (balanceEditOptions[0]?.label || '')}
                      setSelectedOption={(value) => {
                        // Find the original value (with underscores) from the formatted label
                        const originalValue = balanceEditOptions.find(opt => opt.label === value)?.value || value;
                        handleInputChange(originalValue, "balance");
                      }}
                      fullWidth={true}
                    />
                  ) : (
                    <p>{formatDisplayName(benchmark.balance)}</p>
                  )}
                </div>
                <div className="benchmark-category">
                  {benchmarkEditIndex === key ? (
                    // Always use text input for readability balance
                    benchmarkEditData?.balance === "readability" ? (
                      <Input
                        type="text"
                        value={benchmarkEditData?.category}
                        onChange={(e) => handleInputChange(e.target.value, "category")}
                        placeholder="e.g., neutraal, moeilijk, gemakkelijk"
                      />
                    ) : loadingCategories ? (
                      <p>Loading categories...</p>
                    ) : categories.length > 0 ? (
                      <Select
                        options={categoryOptions}
                        selectedOption={formatDisplayName(benchmarkEditData?.category) || (categoryOptions[0]?.label || '')}
                        setSelectedOption={(value) => {
                          // Find the original value (with underscores) from the formatted label
                          const originalValue = categoryOptions.find(opt => opt.label === value)?.value || value;
                          handleInputChange(originalValue, "category");
                        }}
                        fullWidth={true}
                      />
                    ) : (
                      <Input
                        type="text"
                        value={benchmarkEditData?.category}
                        onChange={(e) => handleInputChange(e.target.value, "category")}
                        placeholder="e.g., hou me op de hoogte"
                      />
                    )
                  ) : (
                    <p>{formatDisplayName(benchmark.category)}</p>
                  )}
                </div>
                <div className="benchmark-path">
                  {benchmarkEditIndex === key ? (
                    <Input
                      type="text"
                      value={benchmarkEditData?.path}
                      onChange={(e) => handleInputChange(e.target.value, "path")}
                      placeholder="*"
                      style={{ maxWidth: '100px' }}
                    />
                  ) : (
                    <p>{benchmark.path}</p>
                  )}
                </div>
                <div className="benchmark-range">
                  {benchmarkEditIndex === key ? (
                    <div className="range-slider-container">
                      <div className="range-inputs">
                        <div className="range-input-group">
                          <label>Min:</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={benchmarkEditData.min}
                            onChange={(e) => handleInputChange(parseInt(e.target.value) || 0, "min")}
                            className="range-number-input"
                          />
                          <span>%</span>
                        </div>
                        <div className="range-input-group">
                          <label>Max:</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={benchmarkEditData.max}
                            onChange={(e) => handleInputChange(parseInt(e.target.value) || 0, "max")}
                            className="range-number-input"
                          />
                          <span>%</span>
                        </div>
                      </div>
                      <div className="range-sliders">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={benchmarkEditData.min}
                          onChange={(e) => handleInputChange(parseInt(e.target.value), "min")}
                          className="range-slider range-slider-min"
                        />
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={benchmarkEditData.max}
                          onChange={(e) => handleInputChange(parseInt(e.target.value), "max")}
                          className="range-slider range-slider-max"
                        />
                      </div>
                    </div>
                  ) : (
                    <p>{benchmark.min}% - {benchmark.max}%</p>
                  )}
                </div>
                <div className="benchmark-target">
                  {benchmarkEditIndex === key ? (
                    <div className="range-slider-container">
                      <div className="range-inputs">
                        <div className="range-input-group">
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={benchmarkEditData.target || 50}
                            onChange={(e) => handleInputChange(e.target.value, "target")}
                            className="range-number-input"
                          />
                          <span>%</span>
                        </div>
                      </div>
                      <div className="range-sliders">
                        <input
                          type="range"
                          min="1"
                          max="100"
                          value={benchmarkEditData.target || 50}
                          onChange={(e) => handleInputChange(e.target.value, "target")}
                          className="range-slider"
                        />
                      </div>
                    </div>
                  ) : (
                    <p>{benchmark.target || 50}%</p>
                  )}
                </div>
                <div className="benchmark-readability-range">
                  {benchmarkEditIndex === key && benchmarkEditData.balance === "readability" ? (
                    <div className="readability-range-inputs">
                      <div className="range-input-group">
                        <label>Min:</label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={benchmarkEditData.readabilityRange?.min || 1}
                          onChange={(e) => handleInputChange(parseInt(e.target.value) || 1, "readabilityRangeMin")}
                          className="range-number-input"
                        />
                      </div>
                      <div className="range-input-group">
                        <label>Max:</label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={benchmarkEditData.readabilityRange?.max || 10}
                          onChange={(e) => handleInputChange(parseInt(e.target.value) || 10, "readabilityRangeMax")}
                          className="range-number-input"
                        />
                      </div>
                    </div>
                  ) : benchmark.balance === "readability" && benchmark.readabilityRange ? (
                    <p>{benchmark.readabilityRange.min} - {benchmark.readabilityRange.max}</p>
                  ) : (
                    <p>-</p>
                  )}
                </div>
                {isAdmin && (
                  <div className="benchmark-color">
                    {benchmarkEditIndex === key ? (
                      <div className="color-input-container">
                        <input
                          type="text"
                          value={benchmarkEditData.color || ""}
                          onChange={(e) => handleInputChange(e.target.value, "color")}
                          placeholder="#FF5733"
                          className="color-text-input"
                          style={{ 
                            borderColor: colourError ? '#dc3545' : undefined,
                            width: '80px'
                          }}
                        />
                        <input
                          type="color"
                          value={benchmarkEditData.color || "#000000"}
                          onChange={(e) => handleInputChange(e.target.value, "color")}
                          className="color-picker-input"
                          title="Kies een kleur"
                        />
                        {colourError && (
                          <div className="color-error-message" style={{ 
                            color: '#dc3545', 
                            fontSize: '11px', 
                            marginTop: '-52px',
                            position: 'absolute'
                          }}>
                            {colourError}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="color-display">
                        {benchmark.color ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div 
                              style={{ 
                                width: '20px', 
                                height: '20px', 
                                backgroundColor: benchmark.color,
                                border: '1px solid #ccc',
                                borderRadius: '3px'
                              }}
                              title={benchmark.color}
                            />
                            <span style={{ fontSize: '12px' }}>{benchmark.color}</span>
                          </div>
                        ) : (
                          <p>-</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className="benchmark-buttons">
                  <Button
                    variant="success"
                    size="small"
                    title="Save"
                    onClick={handleBenchmarkSave}
                    style={{ display: benchmarkEditIndex === key ? "" : "none" }}
                  >
                    <SaveIcon />
                  </Button>

                  <Button
                    variant="warning"
                    size="small"
                    title="Cancel"
                    onClick={handleBenchmarkCancel}
                    style={{
                      marginLeft: "10px",
                      marginRight: "10px",
                      display: benchmarkEditIndex === key ? "" : "none",
                    }}
                  >
                    <CloseIcon />
                  </Button>

                  <Button
                    variant="warning"
                    size="small"
                    title="Edit"
                    onClick={() => {
                      setBenchmarkEditData(benchmark);
                      setBenchmarkEditIndex(key);

                      setTimeout(() => {
                        const benchmarkElement = benchmarkBodyRef.current?.children[key] as HTMLElement;
                        if (benchmarkElement && benchmarkBodyRef.current) {
                          const elementTop = benchmarkElement.offsetTop;
                          const containerHeight = benchmarkBodyRef.current.clientHeight;
                          const scrollPosition = elementTop - containerHeight + benchmarkElement.clientHeight + 200;
                          benchmarkBodyRef.current.scrollTop = Math.max(0, scrollPosition);
                        }
                      }, 100);
                    }}
                    style={{ display: benchmarkEditIndex === key ? "none" : "" }}
                  >
                    <EditIcon />
                  </Button>
                  <Button
                    variant="danger"
                    size="small"
                    title="Delete"
                    style={{
                      marginLeft: "10px",
                      marginRight: "10px",
                      display: benchmarkEditIndex === key ? "none" : "",
                    }}
                    onClick={() => handleBenchmarkDelete(key)}
                  >
                    <DeleteIcon />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="benchmark-footer">
          <Button
            variant="success"
            size="small"
            title={benchmarkEditIndex !== null ? "First save or cancel editing" : "Add new benchmark"}
            style={{ marginLeft: "46%", marginRight: "47%" }}
            onClick={handleBenchmarkNew}
            disabled={benchmarkEditIndex !== null}
          >
            <PlusIcon />
          </Button>
        </div>
      </div>

      {!hideActions && (
        <div className="sub-modal-actions">
          <Button
            variant="danger"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={() => {
              handleSave();
              onClose();
            }}
          >
            OK
          </Button>
        </div>
      )}
    </div>
  );
};

export default ChannelOptimalisationModal;
